import { ChangeEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Icon,
  TextField,
  Typography,
  VStack,
} from '@vivaahealth/design-system';
import type { RadioButtonProps } from '@vivaahealth/design-system/dist/components/Input/RadioButton/RadioButton';

import Row from '../Row';

import { prettify } from '../../utils/phoneNumber';
import { RadioButtonCard } from '../RadioButtonCard';

interface BaseGuarantorRadioButtonCardProps {
  radioButtonProps: RadioButtonProps;
}

export interface RowDataProps {
  data: string;
  label: string;
  type: 'phone' | 'address';
}

interface ExistingGuarantorCardProps extends BaseGuarantorRadioButtonCardProps {
  openEditModal: () => void;
  rowData: RowDataProps[];
}

interface SomeoneElseCardProps extends BaseGuarantorRadioButtonCardProps {
  updateAndSaveSomeoneElseValue: (e: ChangeEvent<HTMLInputElement>) => void;
  value: string;
}

const DataRow = ({ data, label, type }: RowDataProps) => {
  if (!data) return null;

  const isPhoneType = type === 'phone';
  const formattedData = isPhoneType ? prettify(data) : data;
  const icon = isPhoneType ? Icon.Phone : Icon.MapPin;

  return (
    <Row key={label} Icon={icon}>
      <VStack gap={4}>
        <Typography variant="paragraph-large">{formattedData}</Typography>
        <Typography color="secondary" variant="title-medium" bold>
          {label}
        </Typography>
      </VStack>
    </Row>
  );
};

export const ExistingGuarantorCard = ({
  openEditModal,
  radioButtonProps,
  rowData,
}: ExistingGuarantorCardProps) => {
  const { t } = useTranslation('common');

  const hasSomeData = rowData.some(({ data }) => data.length > 0);

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    openEditModal();
  };

  return (
    <RadioButtonCard
      radioButtonProps={radioButtonProps}
      cardBody={
        hasSomeData ? (
          <VStack gap={16}>
            {rowData.map((rowDataProps) => (
              <DataRow key={rowDataProps.label} {...rowDataProps} />
            ))}
          </VStack>
        ) : null
      }
      headerCta={
        <Button onClick={handleClick} size="x-small" variant="plain">
          {t('edit')}
        </Button>
      }
    />
  );
};

export const SomeoneElseCard = ({
  radioButtonProps,
  updateAndSaveSomeoneElseValue,
  value,
}: SomeoneElseCardProps) => {
  const { t } = useTranslation('guarantorVerification');
  return (
    <RadioButtonCard
      radioButtonProps={radioButtonProps}
      cardBody={
        <VStack gap={16}>
          <TextField
            onChange={updateAndSaveSomeoneElseValue}
            placeholder={t('someoneElsePlaceholder')}
            value={value}
          />
          <Alert variant="info">{t('someoneElseAlert')}</Alert>
        </VStack>
      }
    />
  );
};
